import React from "react";
import { Document, Page, View, Text } from "@react-pdf/renderer";
import { ReportHeader } from "../components/charting/pdf/shared/ReportHeader";
import { ReportFooter } from "../components/charting/pdf/shared/ReportFooter";
import { ReportTable } from "../components/charting/pdf/shared/ReportTable";
import { reportStyles } from "../components/charting/pdf/shared/styles";
import { getPitchers } from "../components/charting/pdf/shared/utils";
import { processPitchDataForAdvance } from "../components/charting/pdf/shared/pitchProcessing";

export const TTOReport = ({ charts = [] }) => {
  if (!charts.length) {
    return (
      <Document>
        <Page size="A4" style={reportStyles.page}>
          <Text>No data available to generate the report.</Text>
        </Page>
      </Document>
    );
  }

  const pitchers = getPitchers(charts);

  if (!pitchers.length) {
    return (
      <Document>
        <Page size="A4" style={reportStyles.page}>
          <Text>No pitcher data available to generate the report.</Text>
        </Page>
      </Document>
    );
  }

  const calculateTTO = (pitches) => {
    const batterAppearances = {};
    const enrichedPitches = pitches.map((pitch) => ({ ...pitch }));

    enrichedPitches.sort((a, b) => {
      if (a.inning !== b.inning) {
        return (a.inning || 0) - (b.inning || 0);
      }
      return new Date(a.time || 0) - new Date(b.time || 0);
    });

    enrichedPitches.forEach((pitch) => {
      const batterName = pitch.batter?.name;

      if (!batterName) return;

      const inning = pitch.inning;
      const topBottom = pitch.topBottom;

      if (inning === undefined || topBottom === undefined) return;

      if (!batterAppearances[batterName]) {
        batterAppearances[batterName] = {
          count: 1,
          lastInning: inning,
          lastTopBottom: topBottom,
        };
        pitch.tto = 1;
      } else if (
        inning !== batterAppearances[batterName].lastInning ||
        topBottom !== batterAppearances[batterName].lastTopBottom
      ) {
        batterAppearances[batterName].count++;
        batterAppearances[batterName].lastInning = inning;
        batterAppearances[batterName].lastTopBottom = topBottom;
        pitch.tto = batterAppearances[batterName].count;
      } else {
        pitch.tto = batterAppearances[batterName].count;
      }
    });

    return enrichedPitches;
  };

  const pages = pitchers
    .flatMap((pitcherName) => {
      const pitcherPitches = charts.flatMap((chart) =>
        chart.pitches.filter((pitch) => pitch.pitcher?.name === pitcherName)
      );

      if (pitcherPitches.length === 0) return null;

      return ["left", "right"].map((batterHand) => {
        const handFilteredPitches = pitcherPitches.filter(
          (pitch) => pitch.batter?.batHand?.toLowerCase() === batterHand
        );

        if (!handFilteredPitches.length) return null;

        const ttoPitches = calculateTTO(handFilteredPitches);

        const tto1 = ttoPitches.filter((p) => p.tto === 1);
        const tto2 = ttoPitches.filter((p) => p.tto === 2);
        const tto3Plus = ttoPitches.filter((p) => p.tto >= 3);

        return (
          <Page
            key={`${pitcherName}-${batterHand}`}
            size="A4"
            orientation="portrait"
            style={reportStyles.page}
          >
            <ReportHeader
              title={`${pitcherName} - Advance Report`}
              subtitle={`vs ${batterHand === "left" ? "LHH" : "RHH"}`}
            />

            <View style={{ marginBottom: 10 }}>
              <Text
                style={{ fontSize: 14, fontWeight: "bold", marginBottom: 5 }}
              >
                1st Time Through Order ({tto1.length} pitches)
              </Text>
              {tto1.length > 0 ? (
                renderTTOTable(tto1)
              ) : (
                <Text style={reportStyles.emptyMessage}>No data available</Text>
              )}
            </View>

            <View style={{ marginBottom: 10 }}>
              <Text
                style={{ fontSize: 14, fontWeight: "bold", marginBottom: 5 }}
              >
                2nd Time Through Order ({tto2.length} pitches)
              </Text>
              {tto2.length > 0 ? (
                renderTTOTable(tto2)
              ) : (
                <Text style={reportStyles.emptyMessage}>No data available</Text>
              )}
            </View>

            <View style={{ marginBottom: 10 }}>
              <Text
                style={{ fontSize: 14, fontWeight: "bold", marginBottom: 5 }}
              >
                3rd+ Time Through Order ({tto3Plus.length} pitches)
              </Text>
              {tto3Plus.length > 0 ? (
                renderTTOTable(tto3Plus)
              ) : (
                <Text style={reportStyles.emptyMessage}>No data available</Text>
              )}
            </View>
            <ReportFooter />
          </Page>
        );
      });
    })
    .filter(Boolean);

  if (pages.length === 0) {
    return (
      <Document>
        <Page size="A4" style={reportStyles.page}>
          <Text>No data available to generate the report.</Text>
        </Page>
      </Document>
    );
  }

  return <Document>{pages}</Document>;
};

const renderTTOTable = (pitches) => {
  const pitchMetrics = processPitchDataForAdvance(pitches);

  const pitchMetricsArray = Object.entries(pitchMetrics).map(
    ([type, data]) => ({
      type,
      ...data,
    })
  );

  pitchMetricsArray.sort((a, b) => parseFloat(b.usage) - parseFloat(a.usage));

  if (pitchMetricsArray.length === 0) {
    return <Text style={reportStyles.emptyMessage}>No pitch data available</Text>;
  }

  const tableRows = pitchMetricsArray.map((data) => ({
    cells: [
      {
        value:
          data.type.charAt(0).toUpperCase() +
          data.type.slice(1).toLowerCase(),
      },
      { value: data.count },
      { value: `${data.usage}%` },
      { value: data.avgVelo },
      { value: data.maxVelo },
      { value: `${data.zoneRate}%` },
      { value: `${data.cswRate}%` },
      { value: `${data.hardHitRate}%` },
    ],
  }));

  return (
    <ReportTable
      headers={[
        "Pitch Type",
        "Count",
        "Usage %",
        "Avg Velo",
        "Max Velo",
        "Zone%",
        "CSW%",
        "HardHit%",
      ]}
      rows={tableRows}
      cellStyles={{ header: Array(8).fill({ fontSize: 9 }) }}
    />
  );
};


